import { normalize, pushGA4Event } from '@theme/ga4-events';
import { ThemeEvents } from '@theme/events';

function trackHeader(element) {
  if (element.matches('summary') && element.closest('details')?.open) return;
  pushGA4Event({ event_name: 'header', label: element.dataset.gtmLabel ?? '' });
}

function trackMenu(element) {
  pushGA4Event({
    event_name: 'menu',
    label: (element.dataset.gtmLabel ?? '').split('^').map(normalize).join('^'),
    position: element.dataset.gtmPosition ?? '',
  });
}

function trackFooter(element, event) {
  const link = event.target.closest('a[href], a.country_splash_open');
  if (!link || link.getAttribute('aria-disabled') === 'true') return;

  pushGA4Event({
    event_name: 'footer',
    label: normalize(link.textContent ?? link.getAttribute('aria-label') ?? ''),
    type: normalize(link.closest('.menu__details')?.dataset.gtmType ?? ''),
  });
}

function trackSocial(element, event) {
  const link = event.target.closest('a[href]');
  // Social blocks are dynamic and can't get params, the container that renders them sets the label
  const label = element.closest('[data-gtm-social-label]')?.dataset.gtmSocialLabel;
  if (!link || !label) return;

  pushGA4Event({ event_name: 'social', label });
}

function trackAnnouncement(element) {
  if (!element.closest('.announcement-bar__slide[aria-hidden="false"]')) return;
  pushGA4Event({ event_name: 'top_bar', label: normalize(element.dataset.gtmLabel ?? '') });
}

function trackAnnouncementArrow(element, event) {
  const action = event.target.closest('.slideshow-control')?.getAttribute('ref');
  if (!action) return;
  pushGA4Event({ event_name: 'top_bar', label: `arrow_${action}` });
}

function trackLoadMore(element) {
  pushGA4Event({ event_name: 'listing_load_more', label: 'load_more' });
}

function trackMiniCart() {
  pushGA4Event({ event_name: 'mini-cart', label: 'checkout' });
}

function trackVisualFilter(element) {
  pushGA4Event({ event_name: 'listing_visual_filter', label: normalize(element.dataset.gtmLabel ?? '') });
}

function trackFilterChange(element, event) {
  // isTrusted skips the synthetic change fired by the in-stock preselect when the filters drawer opens
  if (event.isTrusted && event.target instanceof HTMLInputElement) trackFilterInput(event.target);
}

function trackSort(element, event) {
  const input = event.target;
  const label = input instanceof HTMLSelectElement ? input.selectedOptions[0]?.textContent : input.dataset.optionName;
  pushGA4Event({ event_name: 'listing_sort_by', label: normalize(label ?? '') });
}

function trackPdpAccordion(element) {
  const productInfo = element.closest('[id*="ProductInformation-"]');
  if (!productInfo) return;

  const details = element.closest('details');
  const label = element.querySelector('h3')?.textContent ?? '';
  const sku = productInfo.querySelector('.gtm-view-item-update')?.dataset.gtmSku?.slice(0, 13) ?? '';

  pushGA4Event({
    event_name: 'pdp_interaction',
    label: normalize(label),
    type: details?.open ? 'close_accordion' : 'open_accordion',
    product: sku,
  });
}

/**
 * Tracks filter removal. Called by the filters, chip and "Clear all" button
 * @param {HTMLElement} element
 */
export function trackFilterRemove(element) {
  pushGA4Event({
    event_name: 'remove_listing_filter',
    label: normalize(element.dataset.gtmLabel ?? ''),
    type: normalize(element.dataset.gtmType ?? ''),
  });
}

/**
 * Tracks filter input changes
 * @param {HTMLInputElement} input
 */
export function trackFilterInput(input) {
  const type = normalize(input.closest('.facets__panel')?.querySelector('.facets__label')?.textContent ?? '');

  if (input.type === 'range') {
    const slider = input.closest('double-range-input');
    const min = slider?.querySelector('.range-slider-input-left');
    const max = slider?.querySelector('.range-slider-input-right');
    if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) return;

    // With step 1 the right handle can stop just below a non-integer max
    const isFullRange = Number(min.value) <= Number(min.min) && Number(max.value) + Number(max.step) > Number(max.max);
    pushGA4Event({
      event_name: isFullRange ? 'remove_listing_filter' : 'add_listing_filter',
      label: `${Math.trunc(Number(min.value))}_${Math.trunc(Number(max.value))}`,
      type,
    });
    return;
  }

  pushGA4Event({
    event_name: input.checked ? 'add_listing_filter' : 'remove_listing_filter',
    label: normalize(input.dataset.label ?? input.getAttribute('aria-label') ?? input.value),
    type,
  });
}

/**
 * Tracks color selection on the listing page.
 * @param {HTMLElement} pill
 */
export function trackListingColorSelection(pill) {
  // Ensure the clicked pill is within the product grid
  if (!pill.closest('.product-grid')) return;

  const swatches = pill.closest('.kway-swatches');
  if (!swatches || swatches.dataset.gtmOptionType !== 'color') return;

  const selectedColor = normalize(pill.dataset.gtmColor ?? '');
  const sku = pill.dataset.gtmSku?.slice(0, 13) ?? '';

  pushGA4Event({
    event_name: 'product_attribute_selection',
    label: `color/${selectedColor}`,
    position: 'listing',
    product: sku,
  });
}

/**
 * Tracks color selection on the PDP
 * @param {HTMLElement} pill
 */
export function trackPdpColorSelection(pill) {
  const swatches = pill.closest('.kway-swatches');
  if (!swatches || swatches.dataset.gtmOptionType !== 'color') return;

  const selectedColor = normalize(pill.dataset.gtmColor ?? '');
  const sku = pill.dataset.gtmSku?.slice(0, 13) ?? '';

  pushGA4Event({
    event_name: 'product_attribute_selection',
    label: `color/${selectedColor}`,
    product: sku,
  });
}

// Size guide CTA: 3rd-party app markup, no data-gtm-click available, so match its (open-only) trigger button directly
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('.size-guide-button');
  if (!button) return;

  const sku = button.closest('[id*="ProductInformation-"]')?.querySelector('.gtm-view-item-update')?.dataset.gtmSku;
  pushGA4Event({ event_name: 'pdp_sizeguide_cta', product: (sku ?? '').slice(0, 13) });
});

// Size change on the PDP variant picker
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (!(event.target instanceof Element)) return;
  const productInfo = event.target.closest('[id*="ProductInformation-"]');
  if (!productInfo) return;

  const gtmForm = productInfo.querySelector('.gtm-view-item-update');
  if (!(gtmForm instanceof HTMLElement)) return;

  const isSizeOption = event.target.querySelector('[data-gtm-option-type="size"]');
  if (!isSizeOption) return;

  const size = gtmForm.dataset.gtmSize ?? '';

  pushGA4Event({
    event_name: 'product_attribute_selection',
    label: `size/${normalize(size !== '' ? size : 'data_not_set')}`,
    product: (gtmForm.dataset.gtmSku ?? '').slice(0, 13),
  });
});

// Iubenda preferences link in footer click tracking (otherwise blocked by Iubenda's event handling)
document.querySelectorAll('[data-gtm-click="footer"] .iubenda-cs-preferences-link').forEach((link) => {
  link.addEventListener('click', (event) => trackFooter(link, event));
});

// Newsletter subscription (Klaviyo)
const klaviyoPopupFormIds = new Set();

/**
 * @param {string} formId
 * @returns {'popup' | 'footer' | 'page'}
 */
function getNewsletterPosition(formId) {
  if (klaviyoPopupFormIds.has(formId)) return 'popup';

  return document.querySelector(`.klaviyo-form-${CSS.escape(formId)}`)?.closest('footer') ? 'footer' : 'page';
}

window.addEventListener('klaviyoForms', (event) => {
  const { type, formId } = /** @type {CustomEvent} */ (event).detail ?? {};
  if (!formId) return;

  if (type === 'open') {
    klaviyoPopupFormIds.add(formId);
  } else if (type === 'embedOpen') {
    klaviyoPopupFormIds.delete(formId);
  } else if (type === 'submit') {
    // Klaviyo's JS only triggers the 'submit' event after a successful subscription
    pushGA4Event({ event_name: 'newsletter_subscription', position: getNewsletterPosition(formId) });
  }
});

const clickHandlers = {
  header: trackHeader,
  menu: trackMenu,
  footer: trackFooter,
  social: trackSocial,
  top_bar: trackAnnouncement,
  top_bar_arrow: trackAnnouncementArrow,
  listing_load_more: trackLoadMore,
  listing_visual_filter: trackVisualFilter,
  pdp_interaction: trackPdpAccordion,
  mini_cart: trackMiniCart,
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const element = event.target.closest('[data-gtm-click]');
  if (element) clickHandlers[element.dataset.gtmClick]?.(element, event);
});

const changeHandlers = {
  listing_sort_by: trackSort,
  listing_filter: trackFilterChange,
};

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element)) return;
  const element = event.target.closest('[data-gtm-change]');
  if (element) changeHandlers[element.dataset.gtmChange]?.(element, event);
});
